/*
 * Wholesale Portal lock (theme-only, hashed password).
 *
 * The password is never stored in the page. The theme holds only a salted
 * SHA-256 hash ("salt$hash", generated with tools/wholesale-password-hash.html).
 * On submit we hash the entered value with the same salt and compare. Repeated
 * wrong attempts trigger a short lockout. A successful unlock is remembered for
 * the browser session.
 *
 * This is a front-end gate: it keeps the password out of the source and deters
 * casual visitors, but a determined user with dev tools can still bypass a
 * client-side lock. For per-customer security use Shopify customer accounts.
 *
 * Entry points:
 *   1. Hero-banner box ([data-wh-unlock-form]) -> unlock, then go to the portal.
 *   2. Portal lock overlay ([data-wh-lock])     -> unlock + reveal in place.
 */
(function () {
  'use strict';

  var HINT = 'wh-portal-unlock';
  var FAILS = 'wh-lock-fails';
  var UNTIL = 'wh-lock-until';
  var MAX_TRIES = 5;        // wrong attempts before a cooldown
  var COOLDOWN_MS = 30000;  // 30s lockout once the limit is hit

  function setHint() { try { sessionStorage.setItem(HINT, '1'); } catch (e) {} }
  function hasHint() { try { return sessionStorage.getItem(HINT) === '1'; } catch (e) { return false; } }

  function getNum(k) { try { return parseInt(localStorage.getItem(k) || '0', 10) || 0; } catch (e) { return 0; } }
  function setNum(k, v) { try { localStorage.setItem(k, String(v)); } catch (e) {} }
  function clearFails() { try { localStorage.removeItem(FAILS); localStorage.removeItem(UNTIL); } catch (e) {} }

  function lockedFor() { return Math.max(0, getNum(UNTIL) - Date.now()); }

  function registerFail() {
    var n = getNum(FAILS) + 1;
    setNum(FAILS, n);
    if (n >= MAX_TRIES) { setNum(UNTIL, Date.now() + COOLDOWN_MS); setNum(FAILS, 0); }
  }

  function hex(buf) {
    return [].map.call(new Uint8Array(buf), function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
  }

  function sha256Hex(str) {
    if (!(window.crypto && crypto.subtle)) return Promise.resolve(null);
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str)).then(hex);
  }

  function eq(a, b) {
    if (typeof a !== 'string' || typeof b !== 'string' || a.length !== b.length) return false;
    var d = 0;
    for (var i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return d === 0;
  }

  // Resolves true when `password` matches the stored "salt$hash".
  function check(stored, password) {
    stored = (stored || '').trim();
    var i = stored.indexOf('$');
    if (i < 0) return Promise.resolve(false);
    var salt = stored.slice(0, i);
    var target = stored.slice(i + 1).toLowerCase();
    return sha256Hex(salt + password).then(function (h) { return h && eq(h, target); });
  }

  function wireForm(opts) {
    // opts: { form, input, error, stored, button, onSuccess }
    opts.form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (opts.error) opts.error.hidden = true;

      var wait = lockedFor();
      if (wait > 0) {
        if (opts.error) {
          opts.error.textContent = 'Too many attempts. Try again in ' + Math.ceil(wait / 1000) + 's.';
          opts.error.hidden = false;
        }
        return;
      }

      if (opts.button) opts.button.disabled = true;
      check(opts.stored, opts.input ? opts.input.value : '').then(function (ok) {
        if (ok) {
          clearFails();
          setHint();
          opts.onSuccess();
          return;
        }
        registerFail();
        // Small delay slows scripted guessing.
        setTimeout(function () {
          if (opts.button) opts.button.disabled = false;
          if (opts.error) {
            opts.error.textContent = lockedFor() > 0
              ? 'Too many attempts. Try again in ' + Math.ceil(lockedFor() / 1000) + 's.'
              : 'Incorrect password. Please try again.';
            opts.error.hidden = false;
          }
          if (opts.input) { opts.input.focus(); opts.input.select(); }
        }, 400);
      });
    });
  }

  /* 1) Hero-banner box -> unlock, then redirect to the portal. */
  document.querySelectorAll('[data-wh-unlock-form]').forEach(function (form) {
    var target = form.getAttribute('data-wh-target') || '/pages/wholesale-portal';
    wireForm({
      form: form,
      input: form.querySelector('[data-wh-unlock-input]'),
      error: form.parentNode.querySelector('[data-wh-unlock-error]'),
      button: form.querySelector('[type=submit]'),
      stored: form.getAttribute('data-wh-hash'),
      onSuccess: function () { window.location.href = target; }
    });
  });

  /* 2) Portal lock overlay. */
  var lock = document.querySelector('[data-wh-lock]');
  if (lock) {
    var stored = (lock.getAttribute('data-wh-hash') || '').trim();

    function reveal() {
      lock.style.display = 'none';
      document.documentElement.classList.remove('wh-locked');
    }

    // No hash configured -> nothing to check; don't trap the merchant's own page.
    if (!stored) { reveal(); return; }

    if (hasHint()) { reveal(); }
    else { lock.style.display = ''; document.documentElement.classList.add('wh-locked'); }

    var lform = lock.querySelector('[data-wh-lock-form]');
    if (lform) {
      wireForm({
        form: lform,
        input: lock.querySelector('[data-wh-lock-input]'),
        error: lock.querySelector('[data-wh-lock-error]'),
        button: lform.querySelector('[type=submit]'),
        stored: stored,
        onSuccess: reveal
      });
    }
  }
})();
